import { Box, Typography } from "@mui/material";
import CoordinatorAvatars from "../_components/CoordinatorAvatars";
import HighlightStar from "../_components/HighlightStar";
import ReadinessBar from "./ReadinessBar";
import { clientLabel, dateRangeLabel, NEON, placeLabel, THEME, whenLabel } from "./boardStyles";
import type { BoardShow } from "./boardTypes";

type Props = {
  shows: BoardShow[];
  max: number;
  onOpen: (show: BoardShow) => void;
  onToggleHighlight: (show: BoardShow) => void;
};

/**
 * "Highlighted": the shows this person starred (up to 5), any date, always on top
 * with the ShowPhaze neon border. Only rendered when there is at least one.
 */
export default function HighlightedStrip({ shows, max, onOpen, onToggleHighlight }: Props) {
  if (!shows.length) return null;
  return (
    <Box component="section" aria-label="Highlighted shows">
      <Box sx={{ display: "flex", alignItems: "baseline", gap: "14px", mb: "14px", flexWrap: "wrap" }}>
        <Typography variant="h4" component="h2">
          Highlighted
        </Typography>
        <Typography sx={{ fontSize: "15px", opacity: 0.8 }}>
          {shows.length} of {max} · only you see these
        </Typography>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "16px" }}>
        {shows.map((show) => (
          <HighlightCard key={show._id} show={show} onOpen={onOpen} onToggle={() => onToggleHighlight(show)} />
        ))}
      </Box>
    </Box>
  );
}

function HighlightCard({ show, onOpen, onToggle }: { show: BoardShow; onOpen: (s: BoardShow) => void; onToggle: () => void }) {
  const subtitle = [clientLabel(show), placeLabel(show)].filter(Boolean).join(" · ");
  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: "12px",
        background: THEME.black,
        border: NEON.border,
        boxShadow: NEON.glow,
        minWidth: 0,
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => onOpen(show)}
        aria-label={`${show.eventName}: ${whenLabel(show)}, ${dateRangeLabel(show)}`}
        sx={{
          all: "unset",
          boxSizing: "border-box",
          cursor: "pointer",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "14px 16px",
          borderRadius: "12px",
          "&:hover": { background: "rgba(255, 61, 245, 0.06)" },
          "&:focus-visible": { outline: `2px solid ${THEME.white}`, outlineOffset: "2px" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", pr: "30px", minWidth: 0 }}>
          <Typography sx={{ fontSize: "15px", fontWeight: 600, color: NEON.color, whiteSpace: "nowrap" }}>
            {whenLabel(show)}
          </Typography>
          <Typography sx={{ fontSize: "14px", opacity: 0.7, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            · {dateRangeLabel(show)}
          </Typography>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            title={show.eventName}
            sx={{ fontSize: "18px", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {show.eventName}
          </Typography>
          {subtitle ? (
            <Typography sx={{ fontSize: "14px", opacity: 0.7, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <ReadinessBar show={show} />
          </Box>
          <CoordinatorAvatars coordinators={show.coordinators} slots={3} size={24} disableTooltips />
        </Box>
      </Box>
      <Box sx={{ position: "absolute", top: 8, right: 8 }}>
        <HighlightStar on showName={show.eventName} onToggle={onToggle} />
      </Box>
    </Box>
  );
}
